import { useCallback } from 'react';
import { useAwsRum } from './awsrum';

export interface ErrorContext {
    /** Error category (e.g., 'api', 'validation', 'payment', 'render') */
    category?: string;
    /** Specific operation that failed (e.g., 'submit_form', 'load_data') */
    operation?: string;
    /** Organization ID if available */
    organizationId?: string;
    /** Additional metadata */
    metadata?: Record<string, any>;
    /** Severity level */
    severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface UseErrorTrackingReturn {
    /** Track an error with optional context */
    trackError: (error: Error | string, context?: ErrorContext) => void;
}

export const useErrorTracking = (): UseErrorTrackingReturn => {
    const awsRum = useAwsRum();

    const trackError = useCallback(
        (error: Error | string, context?: ErrorContext) => {
            let errorObject = typeof error === 'string' ? new Error(error) : error;

            const enhancedContext = {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                pathname: window.location.pathname,
                userAgent: navigator.userAgent,
                category: context?.category || 'general',
                severity: context?.severity || 'medium',
                ...context,
            };

            // Enhance the error message with context information
            const contextString = JSON.stringify(enhancedContext);
            if (typeof error === 'string') {
                errorObject = new Error(`${error} | Context: ${contextString}`);
            } else {
                // Append context to the existing error message
                errorObject.message = `${errorObject.message} | Context: ${contextString}`;
            }

            // Send to AWS RUM (only pass the error object)
            awsRum?.recordError(errorObject);

            // Log to console in development
            if (process.env.NODE_ENV === 'development') {
                console.group(`🚨 Error Tracked: ${enhancedContext.category}`);
                console.error('Error:', errorObject);
                console.info('Context:', enhancedContext);
                console.groupEnd();
            }
        },
        [awsRum]
    );

    return {
        trackError,
    };
};

